import { useState, useEffect } from "react";
import { supabase } from "./supabaseClient";
import { useAuth } from "./AuthContext";
import ImageViewer from "./ImageViewer";
import { LevelBadge, MannerBadge } from "./Badges";
import { formatSportLevel, formatRecordLevel, formatRecord, formatManner } from "./utils/teamLevel";
import pickleballIcon from "./pickleball.png";
import TeamSelectModal from "./components/TeamSelectModal";

const SPORTS = {
  tennis:      { icon: "🎾", label: "테니스" },
  soccer:      { icon: "⚽", label: "축구" },
  futsal:      { icon: "🥅", label: "풋살" },
  badminton:   { icon: "🏸", label: "배드민턴" },
  basketball:  { icon: "🏀", label: "농구" },
  bowling:     { icon: "🎳", label: "볼링" },
  tabletennis: { icon: "🏓", label: "탁구" },
  baseball:    { icon: "⚾", label: "야구" },
  golf:        { icon: "⛳", label: "골프" },
  running:     { icon: null, label: "피클볼" },
  billiards:   { icon: "🎱", label: "당구" },
};

function parseAgeGroup(ag) {
  if (!ag) return "";
  if (Array.isArray(ag)) return ag.join(", ");
  try { const p = JSON.parse(ag); return Array.isArray(p) ? p.join(", ") : ag; } catch { return ag; }
}

function parseArray(val) {
  if (!val) return [];
  if (Array.isArray(val)) return val;
  try { const p = JSON.parse(val); return Array.isArray(p) ? p : []; } catch { return []; }
}

function normalizeSportsType(type) {
  if (type === "tabletennis") return "table_tennis";
  if (type === "running")     return "pickleball";
  return type || "";
}

function formatSportDetail(rawType, cd) {
  if (!cd) return null;
  const t = normalizeSportsType(rawType);
  switch (t) {
    case "tennis":       return cd.ntrp ? `NTRP ${cd.ntrp}` : null;
    case "soccer":
    case "futsal":       return cd.position || null;
    case "badminton":    return cd.level || null;
    case "basketball":   return [cd.position, cd.level].filter(Boolean).join(" · ") || null;
    case "bowling":      return cd.avg ? `AVG ${cd.avg}` : null;
    case "table_tennis": return cd.level || null;
    case "baseball":     return cd.position || null;
    case "golf":         return cd.handicap ? `핸디캡 ${cd.handicap}` : null;
    case "pickleball":
    case "billiards":    return cd.years ? `구력 ${cd.years}년` : null;
    default:             return null;
  }
}

const SPORTS_LABEL = {
  tennis: "테니스", soccer: "축구", futsal: "풋살", badminton: "배드민턴",
  basketball: "농구", bowling: "볼링", table_tennis: "탁구", baseball: "야구",
  golf: "골프", pickleball: "피클볼", billiards: "당구",
};
const SPORTS_ICON = {
  tennis: "🎾", soccer: "⚽", futsal: "🥅", badminton: "🏸",
  basketball: "🏀", bowling: "🎳", table_tennis: "🏓", baseball: "⚾",
  golf: "⛳", pickleball: "🏸", billiards: "🎱",
};

export default function TeamProfile({ team, onBack, onLoginRequired }) {
  const { user, activeTeam } = useAuth();
  const [showModal,    setShowModal]    = useState(false);
  const [message,      setMessage]      = useState("");
  const [applyLoading, setApplyLoading] = useState(false);
  const [applyError,   setApplyError]   = useState("");
  const [applyDone,    setApplyDone]    = useState(false);
  const [viewerSrc,    setViewerSrc]    = useState(null);
  // 팀장 팀 목록 + 신청 시 선택된 팀
  const [myLeaderTeams,  setMyLeaderTeams]  = useState([]);
  const [applyTeam,      setApplyTeam]      = useState(null);
  const [showTeamSelect, setShowTeamSelect] = useState(false);
  const [myMembership,   setMyMembership]   = useState(null); // 이 팀의 내 멤버 여부
  // 팀원 목록
  const [teamMembers,    setTeamMembers]    = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [modalProfile,   setModalProfile]   = useState(null);
  const [modalSports,    setModalSports]    = useState([]);
  const [modalLoading,   setModalLoading]   = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    Promise.all([
      supabase.from("teams").select("*").eq("user_id", user.id),
      supabase.from("team_members").select("id").eq("team_id", team.id).eq("user_id", user.id).eq("status", "accepted").maybeSingle(),
    ]).then(([{ data: lt }, { data: mb }]) => {
      setMyLeaderTeams(lt || []);
      setMyMembership(mb || null);
    });
  }, [user?.id, team.id]); // eslint-disable-line

  // 팀원 목록 로드
  useEffect(() => {
    if (!team?.id || !team?.sports_type) return;
    setMembersLoading(true);
    supabase.from("team_members").select("id, user_id, nickname").eq("team_id", team.id).eq("status", "accepted")
      .then(async ({ data: rows }) => {
        if (!rows || rows.length === 0) { setTeamMembers([]); setMembersLoading(false); return; }
        const userIds    = rows.map(r => r.user_id);
        const normalType = normalizeSportsType(team.sports_type);
        const [{ data: profiles }, { data: sports }] = await Promise.all([
          supabase.from("user_profiles").select("id, nickname, profile_image, gender, is_profile_public").in("id", userIds),
          supabase.from("user_sports").select("user_id, career_detail, is_player").in("user_id", userIds).eq("sports_type", normalType),
        ]);
        const profileMap = Object.fromEntries((profiles || []).map(p => [p.id, p]));
        const sportMap   = Object.fromEntries((sports   || []).map(s => [s.user_id, s]));
        setTeamMembers(rows.map(r => ({
          id: r.id, user_id: r.user_id,
          team_nick: r.nickname || null,
          profile: profileMap[r.user_id] || null,
          sport:   sportMap[r.user_id]   || null,
        })));
        setMembersLoading(false);
      });
  }, [team.id, team.sports_type]); // eslint-disable-line

  // 팀원 클릭 시 상세 프로필 로드
  useEffect(() => {
    if (!selectedMember) return;
    setModalLoading(true);
    const normalType = normalizeSportsType(team?.sports_type || "");
    Promise.all([
      supabase.from("user_profiles").select("*").eq("id", selectedMember.user_id).maybeSingle(),
      supabase.from("user_sports").select("*").eq("user_id", selectedMember.user_id).eq("sports_type", normalType),
    ]).then(([{ data: fp }, { data: sp }]) => {
      setModalProfile(fp || selectedMember.profile || null);
      setModalSports(sp || []);
      setModalLoading(false);
    });
  }, [selectedMember]); // eslint-disable-line

  const sport        = SPORTS[team.sports_type] || { icon: "🏅", label: team.sports_type };
  const totalMembers = (team.male_count || 0) + (team.female_count || 0);
  const availDays    = parseArray(team.available_days);
  const availTimes   = parseArray(team.available_times);

  const closeModal = () => setShowModal(false);

  const openModal = () => {
    if (!user?.id) { onLoginRequired?.(); return; }
    if (myLeaderTeams.length === 0) {
      alert("교류전 신청은 팀장만 할 수 있어요. 팀을 먼저 만들어주세요.");
      return;
    }
    if (myLeaderTeams.length === 1) {
      setApplyTeam(myLeaderTeams[0]);
      setMessage(""); setApplyError(""); setApplyDone(false);
      setShowModal(true);
    } else {
      setShowTeamSelect(true);
    }
  };

  const handleTeamSelected = (t) => {
    setApplyTeam(t);
    setShowTeamSelect(false);
    setMessage(""); setApplyError(""); setApplyDone(false);
    setShowModal(true);
  };

  const submitApply = async () => {
    if (!applyTeam?.id)           { setApplyError("팀을 선택해주세요"); return; }
    if (applyTeam.id === team.id) { setApplyError("자기 팀에는 신청할 수 없어요"); return; }
    setApplyLoading(true);
    setApplyError("");
    const { data: appData, error } = await supabase.from("applications").insert([{
      host_team_id:  team.id,
      guest_team_id: applyTeam.id,
      message:       message.trim() || null,
      status:        "pending",
    }]).select().single();
    setApplyLoading(false);
    if (error) {
      setApplyError("신청 중 오류가 발생했어요: " + error.message);
    } else {
      await supabase.from("notifications").insert([{
        team_id:    team.id,
        type:       "match_request",
        title:      "⚡ 새 매칭 신청이 왔어요!",
        body:       `${applyTeam.team_name} 팀이 매칭을 신청했어요`,
        related_id: appData?.id || null,
      }]);
      setApplyDone(true);
    }
  };

  // 내가 이 팀의 팀장이거나 팀원이면 신청 버튼 숨김
  const isMyTeam = myLeaderTeams.some(t => t.id === team.id) || !!myMembership;

  return (
    <div style={s.container}>
      {/* 헤더 */}
      <div style={s.header}>
        <button style={s.backBtn} onClick={onBack}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
            stroke="#E24B4A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
          뒤로
        </button>
        <span style={s.headerTitle}>팀 프로필</span>
        <div style={{ width: 64 }} />
      </div>

      <div style={s.body}>
        {/* 히어로 카드 */}
        <div style={s.heroCard}>
          {/* 팀 로고 or 종목 이모지 */}
          {team.logo_url ? (
            <img
              src={team.logo_url}
              alt="팀 로고"
              loading="lazy"
              style={s.logoImg}
              onClick={() => setViewerSrc(team.logo_url)}
            />
          ) : (
            <div style={s.sportEmoji}>{sport.icon || <img src={pickleballIcon} alt="피클볼" style={{ width: "100%", height: "100%", objectFit: "contain" }} />}</div>
          )}
          <div style={s.teamName}>{team.team_name}</div>
          <div style={s.sportLabel}>{sport.label}</div>
          <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap", justifyContent: "center" }}>
            <div style={s.levelChip}>{team.level}</div>
            {team.skill_level && <div style={s.skillChip}>{team.skill_level}</div>}
          </div>
          {(team.match_level != null || team.manner_avg != null) && (
            <div style={{ display: "flex", gap: 8, marginTop: 10, justifyContent: "center" }}>
              {team.match_level != null && <LevelBadge level={team.match_level} />}
              {team.manner_avg != null && <MannerBadge score={team.manner_avg} />}
            </div>
          )}
          {team.region && (
            <div style={{ fontSize: 13, color: "#9ca3af", marginTop: 10 }}>📍 서울 {team.region}</div>
          )}

          {/* 전적 배지 */}
          {(team.wins != null || team.losses != null || team.draws != null) && (
            <div style={s.recordRow}>
              <RecordBadge value={team.wins   || 0} label="승" color="#16a34a" bg="#f0fdf4" border="#bbf7d0" />
              <RecordBadge value={team.draws  || 0} label="무" color="#d97706" bg="#fffbeb" border="#fde68a" />
              <RecordBadge value={team.losses || 0} label="패" color="#E24B4A" bg="#fff5f5" border="#fecaca" />
            </div>
          )}
        </div>

        {/* 단체사진 */}
        {team.team_photo && (
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#374151", marginBottom: 8 }}>📸 팀 단체사진</div>
            <img
              src={team.team_photo}
              alt="팀 단체사진"
              loading="lazy"
              style={s.teamPhoto}
              onClick={() => setViewerSrc(team.team_photo)}
            />
          </div>
        )}

        {/* 팀 정보 */}
        <div style={s.infoCard}>
          <InfoRow label="구력 레벨" value={formatSportLevel(team.sports_type, team.level)} />
          {team.skill_level && <InfoRow label="실력" value={team.skill_level} />}
          <InfoRow label="전적 레벨" value={formatRecordLevel(team)} />
          <InfoRow label="전적" value={formatRecord(team)} />
          <InfoRow label="매너 점수" value={formatManner(team)} />
          <InfoRow label="총 인원"   value={`${totalMembers}명`} />
          <InfoRow label="성별 구성" value={team.gender_ratio || "-"} />
          <InfoRow label="연령대"    value={parseAgeGroup(team.age_group) || "-"} />
          <InfoRow label="평균 구력" value={team.member_avg_career || "-"} />
          {team.main_place && (
            <InfoRow label="주 활동 장소" value={team.main_place} />
          )}
          {availDays.length > 0 && (
            <InfoRow label="매칭 가능 요일" value={availDays.join(", ")} />
          )}
          {availTimes.length > 0 && (
            <InfoRow label="매칭 가능 시간" value={availTimes.join(", ")} />
          )}
          {team.parking != null && (
            <InfoRow
              label="주차"
              value={team.parking
                ? `가능${team.parking_count ? ` (${team.parking_count}대)` : ""}`
                : "불가능"}
            />
          )}
        </div>

        {/* 팀 소개 */}
        <div style={s.introCard}>
          <div style={s.introTitle}>팀 소개</div>
          <div style={s.introText}>{team.intro || "아직 팀 소개글이 없어요."}</div>
        </div>

        {/* 팀원 목록 */}
        <div style={s.memberSection}>
          <style>{`@keyframes tpSpin{to{transform:rotate(360deg)}}`}</style>
          <div style={s.memberTitle}>팀원 ({teamMembers.length}명)</div>
          {membersLoading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: 20 }}>
              <div style={{ width: 24, height: 24, borderRadius: "50%", border: "3px solid #f0f0f0", borderTop: "3px solid #E8412A", animation: "tpSpin 0.8s linear infinite" }} />
            </div>
          ) : teamMembers.length === 0 ? (
            <p style={{ fontSize: 13, color: "#9ca3af", textAlign: "center", padding: "16px 0", margin: 0 }}>아직 팀원이 없어요</p>
          ) : (
            teamMembers.map(m => (
              <TPMemberCard key={m.id} member={m} sportsType={team.sports_type} onClick={() => setSelectedMember(m)} />
            ))
          )}
        </div>
      </div>

      {/* 하단 신청 버튼 — 내 팀이면 숨김 */}
      {!isMyTeam && (
        <div style={s.footer}>
          <button style={s.applyBtn} onClick={openModal}>
            ⚡ 매칭 신청하기
          </button>
        </div>
      )}

      {viewerSrc && <ImageViewer src={viewerSrc} onClose={() => setViewerSrc(null)} />}

      {selectedMember && (
        <TPMemberModal
          member={selectedMember}
          profile={modalProfile}
          sports={modalSports}
          loading={modalLoading}
          onClose={() => setSelectedMember(null)}
        />
      )}

      {showTeamSelect && (
        <TeamSelectModal
          teams={myLeaderTeams}
          onSelect={handleTeamSelected}
          onClose={() => setShowTeamSelect(false)}
        />
      )}

      {showModal && (
        <div style={s.backdrop} onClick={closeModal}>
          <div style={s.modalBox} onClick={(e) => e.stopPropagation()}>
            {applyDone ? (
              <div style={{ textAlign: "center", padding: "8px 0" }}>
                <div style={{ fontSize: 48, marginBottom: 12 }}>🎉</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: "#111827", marginBottom: 8 }}>신청 완료!</div>
                <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 4 }}>
                  <span style={{ color: "#E24B4A", fontWeight: 700 }}>{team.team_name}</span>에 신청을 보냈어요
                </div>
                <div style={{ fontSize: 12, color: "#9ca3af", marginBottom: 24 }}>
                  '내 매칭' 탭에서 결과를 확인하세요
                </div>
                <button style={s.primaryBtn} onClick={closeModal}>확인</button>
              </div>
            ) : (
              <>
                <div style={{ textAlign: "center", marginBottom: 20 }}>
                  <div style={{ fontSize: 20, marginBottom: 6 }}>⚡</div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: "#111827", marginBottom: 4 }}>매칭 신청</div>
                  <div style={{ fontSize: 13, color: "#6b7280" }}>
                    <span style={{ color: "#E24B4A", fontWeight: 700 }}>{team.team_name}</span>에 신청합니다
                  </div>
                </div>

                {/* 신청 팀 표시 */}
                <div style={{ marginBottom: 16, padding: "12px 14px", background: "#f5f6fa", borderRadius: 10, border: "1px solid #e5e7eb" }}>
                  <div style={{ fontSize: 11, color: "#9ca3af", marginBottom: 4 }}>신청 팀</div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: "#111827" }}>🏅 {applyTeam?.team_name}</div>
                </div>

                {/* 신청 메시지 */}
                <div style={{ marginBottom: 20 }}>
                  <div style={s.formLabel}>
                    신청 메시지
                    <span style={{ fontSize: 11, color: "#9ca3af", fontWeight: 400, marginLeft: 6 }}>선택</span>
                  </div>
                  <textarea
                    style={{ ...s.textInput, resize: "none", fontSize: 14, lineHeight: 1.6, fontFamily: "inherit" }}
                    rows={3}
                    placeholder="간단한 인사말이나 희망 일정을 남겨보세요"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    maxLength={200}
                  />
                </div>

                {applyError && <p style={{ fontSize: 12, color: "#E24B4A", marginBottom: 12 }}>{applyError}</p>}

                <div style={{ display: "flex", gap: 10 }}>
                  <button style={s.cancelBtn} onClick={closeModal} disabled={applyLoading}>취소</button>
                  <button
                    style={{ ...s.primaryBtn, flex: 1, opacity: applyLoading ? 0.6 : 1 }}
                    onClick={submitApply}
                    disabled={applyLoading}
                  >
                    {applyLoading ? "신청 중..." : "신청하기"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


function RecordBadge({ value, label, color, bg, border }) {
  return (
    <div style={{ textAlign: "center", padding: "8px 16px", background: bg, border: `1px solid ${border}`, borderRadius: 10 }}>
      <div style={{ fontSize: 20, fontWeight: 800, color }}>{value}</div>
      <div style={{ fontSize: 11, color, fontWeight: 600, marginTop: 1 }}>{label}</div>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 0", borderBottom: "1px solid #f0f1f5" }}>
      <span style={{ fontSize: 13, color: "#9ca3af", flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: 13, color: "#374151", fontWeight: 600, textAlign: "right", marginLeft: 8 }}>{value}</span>
    </div>
  );
}

const s = {
  container:   { minHeight: "100vh", background: "#f5f6fa", display: "flex", flexDirection: "column", maxWidth: 430, margin: "0 auto", fontFamily: "'Noto Sans KR', sans-serif" },
  logoImg:     { width: 90, height: 90, borderRadius: 18, objectFit: "cover", border: "2px solid #e5e7eb", marginBottom: 12, cursor: "pointer", boxShadow: "0 2px 8px rgba(0,0,0,0.10)" },
  teamPhoto:   { width: "100%", height: 200, objectFit: "cover", borderRadius: 14, border: "1px solid #e5e7eb", cursor: "pointer", display: "block" },
  header:      { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "calc(env(safe-area-inset-top) + 12px) 20px 12px", background: "#fff", borderBottom: "1px solid #e5e7eb", position: "sticky", top: 0, zIndex: 10, boxShadow: "0 1px 8px rgba(0,0,0,0.06)" },
  backBtn:     { display: "flex", alignItems: "center", gap: 2, background: "none", border: "none", fontSize: 14, fontWeight: 600, color: "#E24B4A", cursor: "pointer", padding: "4px 0" },
  headerTitle: { fontSize: 16, fontWeight: 700, color: "#111827" },
  body:        { flex: 1, padding: "20px 20px 0", display: "flex", flexDirection: "column", gap: 14 },
  heroCard:    { background: "#fff", borderRadius: 16, border: "1px solid #e5e7eb", padding: "28px 20px", display: "flex", flexDirection: "column", alignItems: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" },
  sportEmoji:  { fontSize: 52, marginBottom: 12 },
  teamName:    { fontSize: 24, fontWeight: 700, color: "#111827", marginBottom: 4, letterSpacing: "-0.5px" },
  sportLabel:  { fontSize: 14, color: "#9ca3af", marginBottom: 4 },
  levelChip:      { padding: "5px 14px", background: "#fff5f5", border: "1px solid #E24B4A", borderRadius: 999, fontSize: 13, fontWeight: 700, color: "#E24B4A" },
  skillChip:      { padding: "5px 14px", background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 999, fontSize: 13, fontWeight: 700, color: "#0369a1" },
  recordRow:      { display: "flex", gap: 10, marginTop: 14, justifyContent: "center" },
  infoCard:    { background: "#fff", borderRadius: 14, border: "1px solid #e5e7eb", padding: "4px 16px", boxShadow: "0 1px 6px rgba(0,0,0,0.04)" },
  introCard:    { background: "#fff", borderRadius: 14, border: "1px solid #e5e7eb", padding: "16px", boxShadow: "0 1px 6px rgba(0,0,0,0.04)" },
  introTitle:   { fontSize: 13, fontWeight: 700, color: "#374151", marginBottom: 8 },
  introText:    { fontSize: 14, color: "#6b7280", lineHeight: 1.7 },
  memberSection:{ paddingBottom: 24 },
  memberTitle:  { fontSize: 14, fontWeight: 700, color: "#374151", marginBottom: 10 },
  footer:       { padding: "12px 20px 32px", borderTop: "1px solid #e5e7eb", background: "#fff", marginTop: 14 },
  applyBtn:    { width: "100%", padding: "14px", background: "#E24B4A", color: "#fff", border: "none", borderRadius: 12, fontSize: 16, fontWeight: 700, cursor: "pointer" },
  backdrop:    { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, backdropFilter: "blur(4px)" },
  modalBox:    { width: "90%", maxWidth: 340, background: "#fff", borderRadius: 18, padding: "24px 20px 28px", boxShadow: "0 8px 32px rgba(0,0,0,0.20)", maxHeight: "80vh", overflowY: "auto" },
  formLabel:   { fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 8 },
  textInput:   { width: "100%", padding: "13px 14px", background: "#f5f6fa", border: "1px solid #e5e7eb", borderRadius: 10, color: "#111827", fontSize: 15, outline: "none", boxSizing: "border-box" },
  primaryBtn:  { padding: "13px 0", background: "#E24B4A", color: "#fff", border: "none", borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: "pointer", width: "100%" },
  cancelBtn:   { padding: "13px 20px", background: "#f5f6fa", color: "#6b7280", border: "1px solid #e5e7eb", borderRadius: 10, fontSize: 15, fontWeight: 600, cursor: "pointer" },
};

/* ── 팀원 카드 컴포넌트 ─────────────────────────────── */
function TPMemberCard({ member, sportsType, onClick }) {
  const { profile, sport } = member;
  const nick     = profile?.nickname || member.team_nick || "(닉네임 없음)";
  const initials = nick[0]?.toUpperCase() || "?";
  const genderLabel = profile?.gender === "male" ? "남" : profile?.gender === "female" ? "여" : null;
  const isPlayerLabel = sport ? (sport.is_player ? "선출" : "비선출") : null;
  const level    = formatSportDetail(sportsType, sport?.career_detail);
  const detail   = profile?.is_profile_public
    ? [level, isPlayerLabel, genderLabel].filter(Boolean).join(" · ")
    : null;
  return (
    <div
      style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", background: "#fff", borderRadius: 10, marginBottom: 8, border: "1px solid #f3f4f6", cursor: "pointer" }}
      onClick={onClick}
    >
      {profile?.profile_image ? (
        <img src={profile.profile_image} alt="profile"
          style={{ width: 40, height: 40, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />
      ) : (
        <div style={{ width: 40, height: 40, borderRadius: "50%", background: "#E8412A", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <span style={{ fontSize: 16, fontWeight: 700, color: "#fff" }}>{initials}</span>
        </div>
      )}
      <div>
        <div style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>{nick}</div>
        {detail && <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>{detail}</div>}
      </div>
    </div>
  );
}

/* ── 팀원 상세 모달 (바텀 시트) ─────────────────────── */
function TPMemberModal({ member, profile, sports, loading, onClose }) {
  const nick       = profile?.nickname || member.team_nick || "(닉네임 없음)";
  const initials   = nick[0]?.toUpperCase() || "?";
  const genderChip = profile?.gender === "male" ? "남성" : profile?.gender === "female" ? "여성" : null;
  const genderFull = profile?.gender === "male" ? "남자" : profile?.gender === "female" ? "여자" : null;
  const sp         = sports?.[0] || null;
  const spLabel    = sp ? (SPORTS_LABEL[sp.sports_type] || sp.sports_type) : null;
  const spIcon     = sp ? (SPORTS_ICON[sp.sports_type] || "🏅") : null;
  const spLevel    = sp ? formatSportDetail(sp.sports_type, sp.career_detail) : null;

  return (
    <div style={tpm.backdrop} onClick={onClose}>
      <div style={tpm.sheet} onClick={e => e.stopPropagation()}>
        <button style={tpm.closeBtn} onClick={onClose}>✕</button>
        <div style={tpm.profileTop}>
          {profile?.profile_image ? (
            <img src={profile.profile_image} alt="profile" style={tpm.avatar} />
          ) : (
            <div style={tpm.avatarFallback}>
              <span style={tpm.avatarInitials}>{initials}</span>
            </div>
          )}
          <div style={tpm.nickName}>{nick}</div>
          <div style={tpm.metaRow}>
            {genderChip  && <span style={tpm.metaChip}>{genderChip}</span>}
            {profile?.region && <span style={tpm.metaChip}>📍 {profile.region}</span>}
          </div>
          {profile?.awards && <div style={tpm.awards}>🏆 {profile.awards}</div>}
        </div>
        <div style={{ fontSize: 12, fontWeight: 600, color: "#9ca3af", marginBottom: 10 }}>선수 정보</div>
        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: "16px 0" }}>
            <div style={{ width: 24, height: 24, borderRadius: "50%", border: "3px solid #f0f0f0", borderTop: "3px solid #E8412A", animation: "tpSpin 0.8s linear infinite" }} />
          </div>
        ) : !sp ? (
          <div style={{ fontSize: 13, color: "#9ca3af", textAlign: "center", padding: "12px 0" }}>등록된 종목 프로필이 없어요</div>
        ) : (
          <div style={{ background: "#F5F5F7", borderRadius: 10, padding: "0 14px" }}>
            <TPModalRow label="종목" value={`${spIcon} ${spLabel}`} />
            {spLevel && <TPModalRow label="레벨" value={spLevel} />}
            <TPModalRow label="선수 출신" value={sp.is_player ? "선출" : "비선출"} />
            {genderFull && <TPModalRow label="성별" value={genderFull} />}
          </div>
        )}
      </div>
    </div>
  );
}

function TPModalRow({ label, value }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "11px 0", borderBottom: "1px solid #ebebf0" }}>
      <span style={{ fontSize: 12, color: "#9ca3af", flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: 13, color: "#374151", fontWeight: 600, textAlign: "right", marginLeft: 8 }}>{value}</span>
    </div>
  );
}

const tpm = {
  backdrop:       { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "flex-end", justifyContent: "center", zIndex: 300 },
  sheet:          { background: "#fff", borderRadius: "16px 16px 0 0", width: "100%", maxWidth: 430, padding: "24px 20px 48px", position: "relative", maxHeight: "80vh", overflowY: "auto", fontFamily: "'Noto Sans KR', sans-serif" },
  closeBtn:       { position: "absolute", top: 16, right: 16, background: "none", border: "none", fontSize: 18, color: "#6b7280", cursor: "pointer", lineHeight: 1 },
  profileTop:     { display: "flex", flexDirection: "column", alignItems: "center", paddingBottom: 20, borderBottom: "1px solid #f3f4f6", marginBottom: 16 },
  avatar:         { width: 72, height: 72, borderRadius: "50%", objectFit: "cover", marginBottom: 12 },
  avatarFallback: { width: 72, height: 72, borderRadius: "50%", background: "#E8412A", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 12 },
  avatarInitials: { fontSize: 28, fontWeight: 800, color: "#fff" },
  nickName:       { fontSize: 20, fontWeight: 800, color: "#1A1A2E", marginBottom: 8 },
  metaRow:        { display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "center", marginBottom: 4 },
  metaChip:       { padding: "3px 10px", background: "#F5F5F7", borderRadius: 999, fontSize: 12, color: "#374151", fontWeight: 500 },
  awards:         { fontSize: 12, color: "#d97706", marginTop: 6 },
};
